import { useEffect, useMemo, useRef, useState } from 'react';

import { useLokaRef } from 'hooks/UseLokaRef';

import {
  COMPANY_FIELDS,
  FIELDS_TO_CLEAR_AFTER_CITY,
  FIELDS_TO_CLEAR_AFTER_DISTRICT,
  FIELDS_TO_CLEAR_AFTER_HOUSE_NUMBER,
  FIELDS_TO_CLEAR_AFTER_STREET,
  FIELDS_TO_CLEAR_WHILE_TYPING_STREET,
  GERMANY_COUNTRY_CODE,
} from './addressInput.constants';
import {
  buildInitialDisplayOptions,
  buildInitialDistrictOptions,
  createDebouncedSearch,
  getFormikFieldProps,
  hasSelectedCountry,
  mergeStreetSearchOptionsWithSelection,
} from './addressInput.utils';

const toComparableValue = (value) => String(value ?? '');
const toNormalizedInput = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();
const MIN_SEARCH_LENGTH_POSTAL = 5;
const MIN_SEARCH_LENGTH_CITY = 2;
const MIN_SEARCH_LENGTH_STREET = 1;

/** True if the user is likely typing a PLZ (postal code) rather than a city name. */
const inputContainsDigit = (value) => /^\d{5}$/.test(value);

export const useAddressInputController = ({ formik, showErrorsWhenTouched = false }) => {
  const citySearch = useRef(createDebouncedSearch()).current;
  const streetSearch = useRef(createDebouncedSearch()).current;
  const lastCitySearchInputRef = useRef('');
  const lastStreetSearchInputRef = useRef('');
  const lastStreetSearchContextRef = useRef('');

  const {
    lokaRefOptions,
    lokaRefLoadingObjects,
    lokaRefSearchFunctions,
    clearLokaRefOptions,
    abortAll,
  } = useLokaRef();

  const [localDistrictOptions, setLocalDistrictOptions] = useState(() =>
    buildInitialDistrictOptions(formik.values)
  );

  const initialDisplayOptions = useMemo(
    () => buildInitialDisplayOptions(formik.values),
    [formik.values]
  );

  const streetComboOptions = useMemo(
    () => mergeStreetSearchOptionsWithSelection(lokaRefOptions.streetOptions, formik.values),
    [lokaRefOptions.streetOptions, formik.values]
  );
  const effectiveStreetOptions = useMemo(
    () => (streetComboOptions.length ? streetComboOptions : initialDisplayOptions.street),
    [streetComboOptions, initialDisplayOptions.street]
  );

  const hasSelectedCountryValue = hasSelectedCountry(formik.values);
  const hasSelectedPostalCity = Boolean(formik.values?.[COMPANY_FIELDS.POSTAL_CITY_ID]);
  const hasSelectedStreet = Boolean(formik.values?.[COMPANY_FIELDS.STREET_ID]);
  const hasSelectedHouseNumber = Boolean(formik.values?.[COMPANY_FIELDS.HOUSE_NUMBER]);

  useEffect(
    () => () => {
      citySearch.cancel();
      streetSearch.cancel();
      abortAll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only clean up on unmount; abortAll
    // is a new reference every render and would cancel pending debounced searches after each keystroke
    []
  );
  const clearFormikFields = (...names) => {
    names.forEach((name) => formik.setFieldValue(name, '', false));
  };

  const clearDownstreamFromCity = () => {
    citySearch.cancel();
    streetSearch.cancel();
    abortAll();
    lastStreetSearchInputRef.current = '';
    lastStreetSearchContextRef.current = '';
    setLocalDistrictOptions([]);
    clearLokaRefOptions({ districts: true, streets: true, houseNumbers: true });
  };

  const clearDownstreamFromDistrict = () => {
    streetSearch.cancel();
    abortAll();
    clearLokaRefOptions({ streets: true, houseNumbers: true });
  };

  const clearDownstreamFromStreet = () => {
    streetSearch.cancel();
    abortAll();
    lastStreetSearchInputRef.current = '';
    lastStreetSearchContextRef.current = '';
    setLocalDistrictOptions([]);
    clearLokaRefOptions({ streets: true, houseNumbers: true });
  };

  const clearDownstreamWhileTypingStreet = () => {
    streetSearch.cancel();
    abortAll();
  };

  const handlePostalCityInputChange = (event, inputValue, reason) => {
    if (!hasSelectedCountryValue) return false;

    const normalizedInput = toNormalizedInput(inputValue);
    if (reason !== 'input') return false;

    const lastSearchedInput = lastCitySearchInputRef.current;
    const isPLZ = inputContainsDigit(normalizedInput);
    if (!isPLZ && lastSearchedInput && normalizedInput.startsWith(lastSearchedInput)) {
      return false;
    }

    setLocalDistrictOptions([]);
    clearLokaRefOptions({ districts: true, streets: true, houseNumbers: true });
    clearFormikFields(...FIELDS_TO_CLEAR_AFTER_CITY);

    if (!normalizedInput) {
      citySearch.cancel();
      abortAll();
      clearLokaRefOptions({ cities: true });
      lastCitySearchInputRef.current = '';
      return false;
    }

    if (isPLZ || normalizedInput.length >= MIN_SEARCH_LENGTH_CITY) {
      citySearch.cancel();
      abortAll();
      clearLokaRefOptions({ cities: true });
      citySearch.trigger(lokaRefSearchFunctions.searchLokaRefCities, inputValue, (normalized) => {
        const isPLZNorm = inputContainsDigit(normalized);
        return {
          postcode: isPLZNorm ? normalized : '',
          country: formik.values.companyCountry || GERMANY_COUNTRY_CODE,
          city: isPLZNorm ? '' : normalized,
        };
      });
      lastCitySearchInputRef.current = normalizedInput;
      return true;
    }

    citySearch.cancel();
    abortAll();
    clearLokaRefOptions({ cities: true });
    lastCitySearchInputRef.current = '';
    return false;
  };

  const handlePostalCityChange = (event, value) => {
    const selectedCity = lokaRefOptions.postalCityOptions.find(
      (option) => toComparableValue(option.value) === toComparableValue(value)
    );

    lastCitySearchInputRef.current = '';
    clearDownstreamFromCity();
    clearFormikFields(...FIELDS_TO_CLEAR_AFTER_CITY);

    formik.setFieldValue(COMPANY_FIELDS.POSTAL_CITY_ID, selectedCity ? selectedCity.cityId : '');
    formik.setFieldValue(COMPANY_FIELDS.POSTAL_CODE, selectedCity ? selectedCity.postcode : '');
    formik.setFieldValue(COMPANY_FIELDS.CITY, selectedCity ? selectedCity.cityName : '');
    clearLokaRefOptions({ cities: true });
  };

  const handleDistrictInputChange = (event, inputValue, reason) => {
    if (reason !== 'input') return;

    clearDownstreamFromDistrict();
    clearFormikFields(...FIELDS_TO_CLEAR_AFTER_DISTRICT);

    if (!inputValue) {
      setLocalDistrictOptions([]);
    }
  };

  const handleDistrictChange = (event, value) => {
    clearDownstreamFromDistrict();
    clearFormikFields(...FIELDS_TO_CLEAR_AFTER_DISTRICT);

    if (!value) {
      setLocalDistrictOptions([]);
      formik.setFieldValue(COMPANY_FIELDS.DISTRICT_ID, '');
      formik.setFieldValue(COMPANY_FIELDS.DISTRICT, '');
      return;
    }

    formik.setFieldValue(COMPANY_FIELDS.DISTRICT_ID, value);

    const selectedDistrict = localDistrictOptions.find(
      (option) => toComparableValue(option.value) === toComparableValue(value)
    );
    formik.setFieldValue(COMPANY_FIELDS.DISTRICT, selectedDistrict ? selectedDistrict.title : '');
  };

  const handleStreetInputChange = (event, inputValue, reason) => {
    if (!hasSelectedCountryValue) return false;

    if (reason !== 'input') return false;
    const normalizedInput = toNormalizedInput(inputValue);

    const lastSearchedStreetInput = lastStreetSearchInputRef.current;
    const sameStreetContext =
      `${toComparableValue(formik.values[COMPANY_FIELDS.POSTAL_CITY_ID])}|${toComparableValue(
        formik.values[COMPANY_FIELDS.POSTAL_CODE]
      )}` === lastStreetSearchContextRef.current;
    if (
      sameStreetContext &&
      lastSearchedStreetInput &&
      normalizedInput.startsWith(lastSearchedStreetInput)
    ) {
      return false;
    }

    if (!normalizedInput) {
      clearDownstreamFromStreet();
      clearLokaRefOptions({ streets: true });
      clearFormikFields(
        COMPANY_FIELDS.STREET_ID,
        COMPANY_FIELDS.STREET,
        ...FIELDS_TO_CLEAR_AFTER_STREET
      );
      return false;
    }

    clearFormikFields(...FIELDS_TO_CLEAR_WHILE_TYPING_STREET);

    if (normalizedInput.length < MIN_SEARCH_LENGTH_STREET) {
      clearDownstreamWhileTypingStreet();
      clearLokaRefOptions({ streets: true });
      lastStreetSearchInputRef.current = '';
      lastStreetSearchContextRef.current = '';
      return false;
    }

    const currentStreetContext = `${toComparableValue(
      formik.values[COMPANY_FIELDS.POSTAL_CITY_ID]
    )}|${toComparableValue(formik.values[COMPANY_FIELDS.POSTAL_CODE])}`;
    clearDownstreamWhileTypingStreet();
    streetSearch.trigger(lokaRefSearchFunctions.searchLokaRefStreets, inputValue, (normalized) => ({
      street: normalized,
      country: formik.values.companyCountry || GERMANY_COUNTRY_CODE,
      cityId: formik.values[COMPANY_FIELDS.POSTAL_CITY_ID],
      postcode: formik.values[COMPANY_FIELDS.POSTAL_CODE],
    }));
    lastStreetSearchInputRef.current = normalizedInput;
    lastStreetSearchContextRef.current = currentStreetContext;
    return true;
  };

  const handleStreetChange = (event, value) => {
    const selectedStreet = effectiveStreetOptions.find(
      (option) => toComparableValue(option.value) === toComparableValue(value)
    );

    if (!selectedStreet) {
      clearDownstreamFromStreet();
      clearLokaRefOptions({ streets: true });
      clearFormikFields(...FIELDS_TO_CLEAR_AFTER_STREET);
      formik.setFieldValue(COMPANY_FIELDS.STREET_ID, '');
      formik.setFieldValue(COMPANY_FIELDS.STREET, '');
      return;
    }

    lastStreetSearchInputRef.current = '';
    lastStreetSearchContextRef.current = '';
    clearFormikFields(COMPANY_FIELDS.HOUSE_NUMBER, COMPANY_FIELDS.HOUSE_NUMBER_ADDITION);

    formik.setFieldValue(COMPANY_FIELDS.STREET_ID, selectedStreet.streetId);
    formik.setFieldValue(COMPANY_FIELDS.STREET, selectedStreet.streetName);
    clearLokaRefOptions({ streets: true, houseNumbers: true });

    if (selectedStreet.districtId) {
      setLocalDistrictOptions([
        { title: selectedStreet.districtName, value: selectedStreet.districtId },
      ]);
      formik.setFieldValue(COMPANY_FIELDS.DISTRICT_ID, selectedStreet.districtId);
      formik.setFieldValue(COMPANY_FIELDS.DISTRICT, selectedStreet.districtName);
    } else {
      setLocalDistrictOptions([]);
      formik.setFieldValue(COMPANY_FIELDS.DISTRICT_ID, '');
      formik.setFieldValue(COMPANY_FIELDS.DISTRICT, '');
    }
  };

  const handleHouseNumberChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue(COMPANY_FIELDS.HOUSE_NUMBER, value, false);
    if (!String(value ?? '').trim()) {
      clearFormikFields(...FIELDS_TO_CLEAR_AFTER_HOUSE_NUMBER);
    }
  };

  const resolveAndFillAddress = async ({ postalCode, city, street, district }) => {
    const resolved = await lokaRefSearchFunctions.resolveAddressIds({
      postalCode,
      city,
      street,
      district,
    });

    if (!resolved) return false;

    const resolvedValues = {
      [COMPANY_FIELDS.POSTAL_CITY_ID]: resolved.cityId,
      [COMPANY_FIELDS.POSTAL_CODE]: resolved.postcode,
      [COMPANY_FIELDS.CITY]: resolved.cityName,
      [COMPANY_FIELDS.STREET_ID]: resolved.streetId,
      [COMPANY_FIELDS.STREET]: resolved.streetName,
      [COMPANY_FIELDS.DISTRICT_ID]: resolved.districtId ?? '',
      [COMPANY_FIELDS.DISTRICT]: resolved.districtName ?? '',
    };

    if (resolved.districtId) {
      setLocalDistrictOptions([{ title: resolved.districtName, value: resolved.districtId }]);
    } else {
      setLocalDistrictOptions([]);
    }

    formik.resetForm({
      values: {
        ...formik.values,
        ...resolvedValues,
      },
    });

    return resolved;
  };

  useEffect(() => {
    const postal = formik.values[COMPANY_FIELDS.POSTAL_CODE];
    const city = formik.values[COMPANY_FIELDS.CITY];
    if (!postal || !city) return;

    resolveAndFillAddress({
      postalCode: postal,
      city,
      street: formik.values[COMPANY_FIELDS.STREET],
      district: formik.values[COMPANY_FIELDS.DISTRICT],
    });
  }, []);

  const comboProps = {
    postalCity: {
      options: lokaRefOptions.postalCityOptions?.length
        ? lokaRefOptions.postalCityOptions
        : initialDisplayOptions.postalCity,
      label: 'PLZ / Ort',
      filterOptions: (options, state) => {
        const normalized = String(state.inputValue ?? '')
          .trim()
          .toLowerCase();
        if (inputContainsDigit(normalized)) {
          return options;
        }
        if (!normalized) return options;
        if (inputContainsDigit(normalized)) return options;
        return options.filter((option) => (option.title ?? '').toLowerCase().includes(normalized));
      },
      ...getFormikFieldProps({
        formik,
        name: COMPANY_FIELDS.POSTAL_CITY_ID,
        isComboBox: true,
        showErrorsWhenTouched,
      }),
    },
    district: {
      options: localDistrictOptions,
      label: 'Ortsteil',
      ...getFormikFieldProps({
        formik,
        name: COMPANY_FIELDS.DISTRICT_ID,
        isComboBox: true,
        showErrorsWhenTouched,
      }),
    },
    street: {
      options: effectiveStreetOptions,
      label: 'Straße',
      ...getFormikFieldProps({
        formik,
        name: COMPANY_FIELDS.STREET_ID,
        isComboBox: true,
        showErrorsWhenTouched,
      }),
    },
  };

  const fieldProps = {
    houseNumber: {
      label: 'Hausnummer',
      fullWidth: true,
      ...getFormikFieldProps({
        formik,
        name: COMPANY_FIELDS.HOUSE_NUMBER,
        isComboBox: false,
        showErrorsWhenTouched,
      }),
      onChange: handleHouseNumberChange,
    },
    houseNumberAddition: {
      label: 'Hausnummerzusatz',
      ...getFormikFieldProps({
        formik,
        name: COMPANY_FIELDS.HOUSE_NUMBER_ADDITION,
        isComboBox: false,
        showErrorsWhenTouched,
      }),
    },
  };

  const loading = {
    city: lokaRefLoadingObjects.isCitySearchLoading,
    street: lokaRefLoadingObjects.isStreetSearchLoading,
  };

  const handlers = {
    handlePostalCityInputChange,
    handlePostalCityChange,
    handleDistrictInputChange,
    handleDistrictChange,
    handleStreetInputChange,
    handleStreetChange,
  };

  const flags = {
    hasSelectedCountry: hasSelectedCountryValue,
    hasSelectedPostalCity,
    hasSelectedStreet,
    hasSelectedHouseNumber,
  };

  return {
    comboProps,
    fieldProps,
    loading,
    handlers,
    flags,
  };
};
